import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useFormContext } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import MobileInput from "@/components/MobileInput";
import PasswordTextField from "@/components/PasswordTextField";
import { graphql } from "@/gql";
import { Gender } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { gtagEvent } from "@/utils/gtag";
import { muiError, muiField, muiFieldError } from "@/utils/hookForm";

import Footer from "./Footer";
import Header from "./Header";
import Spacer from "../../../../components/layout/Spacer";
import { FormValues } from "../RegisterPage";

const useStyles = makeStyles()({
  mobileInputRoot: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
  },
  required: {
    color: "#D13F3F",
  },
  radioGroup: {
    flexDirection: "row",
    "& .MuiFormControlLabel-root + .MuiFormControlLabel-root": {
      marginLeft: 20,
    },
  },
  redirectToLogin: {
    color: colors.gray,
  },
  redirectToLoginLink: {
    fontWeight: 700,
  },
});

interface Props {
  onNextStep: () => void;
}

const step1FormQuery = graphql(`
  query Step1Form {
    countries {
      alpha2
      isoLongName
      isoShortName
    }
  }
`);

const Step1Form: React.FC<Props> = ({ onNextStep }) => {
  const { classes } = useStyles();

  const {
    control,
    register,
    trigger,
    formState: { errors },
  } = useFormContext<FormValues>();
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] =
    useState<boolean>(false);
  const { data: countriesData, loading: countriesLoading } =
    useQuery(step1FormQuery);
  const handleNext = async () => {
    const valid = await trigger([
      "firstName",
      "middleName",
      "lastName",
      "email",
      "password",
      "dateOfBirth",
      "gender",
      "mobileNumber",
      "country",
      "occupation",
      "expectedNumberOfClientsToHandle",
      "whereDidYouHearAboutNutricoach",
      "otherNutritionManagementSoftwareUsed",
    ]);
    // dont go to next step if this form has errors
    if (valid) {
      gtagEvent("sign_up_attempt");
      onNextStep();
    }
  };

  return (
    <Paper elevation={0} square>
      <Box padding={8} paddingBottom="47px">
        <Header step={1} />
        <Spacer pxVertical={50} />
        <Grid container>
          <Grid item container xs={12} spacing={4}>
            <Grid item xs={4}>
              <TextField
                {...register("firstName")}
                {...muiFieldError(errors.firstName)}
                label={
                  <>
                    First name<span className={classes.required}>*</span>
                  </>
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...register("middleName")}
                {...muiFieldError(errors.middleName)}
                label="Middle name"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...register("lastName")}
                {...muiFieldError(errors.lastName)}
                label={
                  <>
                    Last name<span className={classes.required}>*</span>
                  </>
                }
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={4}>
            <Grid item xs={4}>
              <TextField
                {...register("gender")}
                {...muiFieldError(errors.gender)}
                label={
                  <>
                    Gender<span className={classes.required}>*</span>
                  </>
                }
                defaultValue=""
                select
                fullWidth
              >
                <MenuItem value={Gender.Male}>Male</MenuItem>
                <MenuItem value={Gender.Female}>Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({
                  field: { ref, name, onChange, ...field },
                  fieldState,
                }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    onChange={(value) => onChange(value as Date)}
                    slotProps={{
                      textField: {
                        name,
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...register("email")}
                {...muiFieldError(errors.email)}
                type="email"
                label={
                  <>
                    Email<span className={classes.required}>*</span>
                  </>
                }
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={4}>
            <Grid item xs={4}>
              <TextField
                {...register("country")}
                {...muiFieldError(errors.country)}
                defaultValue=""
                SelectProps={{
                  native: true,
                }}
                select
                fullWidth
                disabled={countriesLoading}
              >
                <option value="" disabled>
                  Select country
                </option>
                {countriesData?.countries?.map((c) => (
                  <option key={c.alpha2} value={c.isoShortName}>
                    {c.isoShortName}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...register("occupation")}
                {...muiFieldError(errors.occupation)}
                label={
                  <>
                    Occupation<span className={classes.required}>*</span>
                  </>
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <PasswordTextField
                {...register("password")}
                {...muiFieldError(errors.password)}
                label={
                  <>
                    Password<span className={classes.required}>*</span>
                  </>
                }
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={4}>
            <Grid item xs={4}>
              <Controller
                control={control}
                name="mobileNumber"
                render={({ field, fieldState }) => (
                  <MobileInput
                    {...muiField(field)}
                    {...muiError(fieldState)}
                    label={
                      <>
                        Mobile number<span className={classes.required}>*</span>
                      </>
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormLabel>
            How many clients are you expecting to handle using the platform?
          </FormLabel>
          <Controller
            name="expectedNumberOfClientsToHandle"
            render={({ field }) => (
              <RadioGroup {...field} className={classes.radioGroup}>
                <FormControlLabel value="1-5" control={<Radio />} label="1-5" />
                <FormControlLabel
                  value="6-10"
                  control={<Radio />}
                  label="6-10"
                />
                <FormControlLabel
                  value="11-15"
                  control={<Radio />}
                  label="11-15"
                />
                <FormControlLabel
                  value="16-20"
                  control={<Radio />}
                  label="16-20"
                />
                <FormControlLabel
                  value="above 21"
                  control={<Radio />}
                  label="above 21"
                />
              </RadioGroup>
            )}
          />
          <FormHelperText>&nbsp;</FormHelperText>
        </Grid>
        <Grid item container xs={12} spacing={4}>
          <Grid item xs={4}>
            <FormLabel>Where did you hear about NutriCoach?</FormLabel>
            <TextField
              {...register("whereDidYouHearAboutNutricoach")}
              label="Select"
              defaultValue=""
              select
              fullWidth
            >
              <MenuItem value="Facebook">Facebook</MenuItem>
              <MenuItem value="Instagram">Instagram</MenuItem>
              <MenuItem value="Referred by a Friend">
                Referred by a Friend
              </MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={8}>
            <FormLabel>
              What other nutrition management software have you tried using?
            </FormLabel>
            <TextField
              {...register("otherNutritionManagementSoftwareUsed")}
              placeholder="Separate answer by comma"
              fullWidth
            />
          </Grid>
        </Grid>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivacyPolicyChecked}
                onChange={() =>
                  setIsPrivacyPolicyChecked(!isPrivacyPolicyChecked)
                }
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle1">
                I agree to the{" "}
                <Link
                  href="https://www.nutricoach.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                >
                  privacy policy
                </Link>
              </Typography>
            }
          />
        </Box>
        <Box marginY="20px">
          <Footer
            onNext={handleNext}
            isPrivacyPolicyChecked={isPrivacyPolicyChecked}
          />
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography color="textSecondary">
            Already have an account?{" "}
            <Link
              className={classes.redirectToLoginLink}
              component={RouterLink}
              to="/login"
            >
              Login
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default Step1Form;
